import { Component, OnInit, OnDestroy, EventEmitter, Output, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { FormGroup, FormBuilder, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { HttpService } from 'src/app/https.service';
import { URLUtils } from '../../urlUtils';
import { ToastrService } from 'ngx-toastr';

function passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
  const newPwd = group.get('new_password')?.value;
  const confirmPwd = group.get('confirm_password')?.value;
  return newPwd && confirmPwd && newPwd !== confirmPwd
    ? { passwordMismatch: true }
    : null;
}

@Component({
  selector: 'app-forgotpwd',
  templateUrl: './forgotpwd.component.html',
  styleUrls: ['./forgotpwd.component.css']
})
export class ForgotpwdComponent implements OnInit, OnDestroy {

  @Output() event = new EventEmitter<any>();
  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef<HTMLInputElement>>;

  /** 1 = email, 2 = OTP verify, 3 = new password */
  step: 1 | 2 | 3 = 1;

  emailForm!: FormGroup;
  pwdForm!: FormGroup;

  submitted = false;
  pwdSubmitted = false;
  isLoading = false;
  msg = '';

  emailValue = '';

  // OTP state
  otpBoxes = [0, 1, 2, 3, 4, 5];   // used only for *ngFor
  otpDigits: string[] = ['', '', '', '', '', ''];
  verifiedOtp = '';

  // Resend countdown
  resendCountdown = 60;
  private countdownInterval: any;

  showNewPassword = false;
  showConfirmPassword = false;

  constructor(
    private formBuilder: FormBuilder,
    private httpservice: HttpService,
    private toast: ToastrService
  ) {}

  ngOnInit(): void {
    this.emailForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.pattern(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/)]]
    });

    this.pwdForm = this.formBuilder.group(
      {
        new_password: ['', [Validators.required, Validators.minLength(8)]],
        confirm_password: ['', Validators.required]
      },
      { validators: passwordMatchValidator }
    );
  }

  ngOnDestroy(): void {
    this.clearCountdown();
  }

  get f() { return this.emailForm.controls; }
  get p() { return this.pwdForm.controls; }

  // ── Helpers ─────────────────────────────────────────────

  private startCountdown(seconds = 60): void {
    this.clearCountdown();
    this.resendCountdown = seconds;
    this.countdownInterval = setInterval(() => {
      this.resendCountdown--;
      if (this.resendCountdown <= 0) {
        this.clearCountdown();
      }
    }, 1000);
  }

  private clearCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  private focusOtpBox(index: number): void {
    // setTimeout gives Angular a tick to render after value changes
    setTimeout(() => {
      const inputs = this.otpInputs?.toArray();
      if (inputs && inputs[index]) {
        inputs[index].nativeElement.focus();
      }
    }, 0);
  }

  get otpValue(): string {
    return this.otpDigits.join('');
  }

  // ── OTP input handlers ───────────────────────────────────

  onOtpInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const val = input.value.replace(/\D/g, '').slice(-1); // digits only, last char
    this.otpDigits[index] = val;
    input.value = val; // keep the DOM in sync

    if (val && index < 5) {
      this.focusOtpBox(index + 1);
    }
  }

  onOtpKeydown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Backspace') {
      if (this.otpDigits[index]) {
        this.otpDigits[index] = '';
      } else if (index > 0) {
        this.otpDigits[index - 1] = '';
        this.focusOtpBox(index - 1);
      }
      event.preventDefault();
    } else if (event.key === 'ArrowLeft' && index > 0) {
      this.focusOtpBox(index - 1);
    } else if (event.key === 'ArrowRight' && index < 5) {
      this.focusOtpBox(index + 1);
    }
  }

  onOtpPaste(event: ClipboardEvent, startIndex: number): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text').replace(/\D/g, '') ?? '';
    pasted.split('').slice(0, 6 - startIndex).forEach((char, i) => {
      this.otpDigits[startIndex + i] = char;
    });
    const nextFocus = Math.min(startIndex + pasted.length, 5);
    this.focusOtpBox(nextFocus);
  }

  // ── Step actions ─────────────────────────────────────────

  cancelml(): void {
    this.clearCountdown();
    this.event.emit({ msg: 'forgot-cancel' });
  }

  /** STEP 1 — submit email */
  onSubmitEmail(): void {
    this.submitted = true;
    this.msg = '';
    if (this.emailForm.invalid) { return; }

    const email = this.emailForm.value['email']?.trim().toLowerCase();
    this.isLoading = true;

    this.httpservice.sendRePostRequest(URLUtils.forgotPwd, { email }).subscribe({
      next: (resp: any) => {
        this.isLoading = false;
        if (resp?.error) {
          this.msg = resp?.msg || 'Failed to send OTP. Please try again.';
        } else {
          this.emailValue = email;
          this.otpDigits = ['', '', '', '', '', ''];
          this.step = 2;
          this.submitted = false;
          this.startCountdown(60);
          // Focus first OTP box after view renders
          setTimeout(() => this.focusOtpBox(0), 100);
        }
      },
      error: (error: any) => {
        this.isLoading = false;
        this.msg = error?.error?.msg || 'Something went wrong. Please try again.';
      }
    });
  }

  /** STEP 2 — verify OTP (client-side length check, then advance to step 3) */
  onVerifyOtp(): void {
    this.msg = '';
    if (this.otpValue.length < 6) {
      this.msg = 'Please enter the complete 6-digit OTP.';
      return;
    }
    // Store the OTP to send with the password reset call
    this.verifiedOtp = this.otpValue;
    this.step = 3;
    this.pwdSubmitted = false;
    this.pwdForm.reset();
  }

  backToOtp(): void {
    this.step = 2;
    this.msg = '';
    this.pwdSubmitted = false;
  }

  resendOtp(): void {
    this.otpDigits = ['', '', '', '', '', ''];
    this.msg = '';
    this.isLoading = true;

    this.httpservice.sendRePostRequest(URLUtils.forgotPwd, { email: this.emailValue }).subscribe({
      next: (resp: any) => {
        this.isLoading = false;
        if (resp?.error) {
          this.msg = resp?.msg || 'Failed to resend OTP.';
        } else {
          this.startCountdown(60);
          this.toast.success('OTP resent successfully.');
          setTimeout(() => this.focusOtpBox(0), 100);
        }
      },
      error: (error: any) => {
        this.isLoading = false;
        this.msg = error?.error?.msg || 'Something went wrong.';
      }
    });
  }

  /** STEP 3 — submit new password */
  onSubmitPassword(): void {
    this.pwdSubmitted = true;
    this.msg = '';
    if (this.pwdForm.invalid) { return; }

    const payload = {
      email: this.emailValue,
      otp: this.verifiedOtp,
      new_password: this.pwdForm.value['new_password']
    };

    this.isLoading = true;

    this.httpservice.sendRePostRequest(URLUtils.confirm, payload).subscribe({
      next: (resp: any) => {
        this.isLoading = false;
        if (resp?.error) {
          // OTP might have expired — send user back to OTP step
          this.msg = resp?.msg || 'Invalid or expired OTP. Please try again.';
          if (resp?.msg?.toLowerCase().includes('otp')) {
            this.step = 2;
            this.otpDigits = ['', '', '', '', '', ''];
          }
        } else {
          this.clearCountdown();
          this.event.emit({ msg: 'forgot-success' });
        }
      },
      error: (error: any) => {
        this.isLoading = false;
        this.msg = error?.error?.msg || 'Something went wrong. Please try again.';
      }
    });
  }
}